import { Ipi } from 'types/ipi.types';
import { Option } from 'types/general.types';
import {
  ProfileFormData,
  RepresentativeFormData,
} from 'app/components/Profile/ProfileForm/types';
import { Ipn } from 'types/ipn.types';
import { Profile } from 'types/profile';
import { defaultToEmptyString, emptyStringToNull } from 'app/utils/utils';
import { defaultPreferredIdOption } from 'app/components/Profile/Ids/utils';
import { Isni } from 'types/isni.types';

export const convertToProfileFormData = (
  data: Profile,
  countries: Option[],
  ipis: Ipi[],
  ipns: Ipn[],
  isnis: Isni[]
): ProfileFormData => {
  const country = countries.find(
    (country) => country.value === data.address?.countryCode
  );
  const preferredIpi = ipis.find(
    (ipi) => ipi.ipiNameNumber === data.preferredIpi?.number
  );
  const preferredIpn = ipns.find(
    (ipn) => ipn.ipnNumber === data.preferredIpn?.number
  );
  const preferredIsni = isnis.find(
    (isni) => isni.isni === data.preferredIsni?.number
  );
  return {
    firstName: data.firstName,
    lastName: data.lastName,
    middleName: defaultToEmptyString(data.middleName),
    displayName: data.displayName,
    country: country ? { title: country.title, value: country.value } : null,
    address: {
      primaryAddress: defaultToEmptyString(data.address?.primaryAddress),
      secondaryAddress: defaultToEmptyString(data.address?.secondaryAddress),
      city: defaultToEmptyString(data.address?.city),
      stateProvinceRegion: defaultToEmptyString(
        data.address?.stateProvinceRegion
      ),
      zipCode: defaultToEmptyString(data.address?.zipCode),
    },
    preferredIpi: data.preferredIpi
      ? {
          title: data.preferredIpi.number.toString(),
          value: data.preferredIpi.number,
          society: preferredIpi?.prSocietyAffiliation?.acronym,
          name: preferredIpi?.profile.displayName,
          verified: preferredIpi?.verified,
        }
      : defaultPreferredIdOption,
    preferredIpn: data.preferredIpn
      ? {
          title: data.preferredIpn.number.toString(),
          value: data.preferredIpn.number,
          society: preferredIpn?.cmoSocietyAffiliation?.acronym,
          name: preferredIpn?.profile.displayName,
          verified: preferredIpn?.verified,
        }
      : defaultPreferredIdOption,
    preferredIsni: data.preferredIsni
      ? {
          title: data.preferredIsni.number.toString(),
          value: data.preferredIsni.number,
          name: preferredIsni?.profile.displayName,
          verified: preferredIsni?.verified,
        }
      : defaultPreferredIdOption,
    privacySettings: data.privacySettings,
    artistName: defaultToEmptyString(data.artistName),
  };
};

export const convertProfileFormDataForBE = (
  data: Partial<ProfileFormData>
) => ({
  firstName: data.firstName,
  lastName: data.lastName,
  middleName: emptyStringToNull(data.middleName),
  displayName: data.displayName,
  address:
    data.address || data.country
      ? {
          countryCode: data?.country?.value,
          primaryAddress: emptyStringToNull(data.address?.primaryAddress),
          secondaryAddress: emptyStringToNull(data.address?.secondaryAddress),
          city: data.address?.city,
          stateProvinceRegion: emptyStringToNull(
            data.address?.stateProvinceRegion
          ),
          zipCode: emptyStringToNull(data.address?.zipCode),
        }
      : undefined,
  preferredIpi: data.preferredIpi?.value,
  preferredIpn: data.preferredIpn?.value,
  preferredIsni: data.preferredIsni?.value,
  privacySettings: data.privacySettings,
  artistName: emptyStringToNull(data.artistName),
});

export const convertRepresentativeFormDataForBE = ({
  firstName,
  lastName,
  company,
  email,
  publisherIpi,
  role,
}: RepresentativeFormData) => ({
  firstName,
  ...(lastName ? { lastName } : {}),
  ...(company ? { company } : {}),
  ...(role ? { role: role.value?.toString() } : {}),
  ...(email ? { email } : {}),
  ...(publisherIpi ? { publisherIpi: +publisherIpi } : {}),
});
